import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Icon, Loading } from '../components/ui';
import { ApiError, acceptInvite, inviteDetails } from '../lib/api';
import { useAuth } from '../lib/auth';
import './Login.css';
import './shared.css';

/**
 * Claiming an invitation: choose a password, and nothing more.
 *
 * The second factor is deliberately not part of this form. Accepting hands
 * back a session that can do exactly one thing — enrol a factor — so the
 * person is carried straight to that screen rather than being asked to absorb
 * two unfamiliar steps at once.
 */
export function AcceptInvitation() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [details, setDetails] = useState<{
    valid: boolean;
    email?: string;
    organisation?: string;
  } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setDetails({ valid: false });
      return;
    }
    inviteDetails(token)
      .then(setDetails)
      .catch(() => setDetails({ valid: false }));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await acceptInvite({ token, name, password });
      await refreshUser();
      // The session cannot reach anything else until a factor is confirmed,
      // and the shell routes there on its own.
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (!details) return <Loading label="Checking your invitation…" />;

  return (
    <div className="login">
      <section className="login__panel">
        <div className="login__inner">
          <div className="login__brand">
            <span className="login__mark">{Icon.logo({ size: 18 })}</span>
            <span className="login__brandname">Fairmount</span>
          </div>

          {!details.valid ? (
            <div className="login__form">
              <h1 className="login__title">That link has expired</h1>
              <p className="login__lede">
                Invitation links are valid for a few days and can be used once.
                Ask whoever invited you to send another — it takes them a moment.
              </p>
              <Button onClick={() => navigate('/login')}>Back to sign in</Button>
            </div>
          ) : (
            <form className="login__form" onSubmit={submit}>
              <h1 className="login__title">Set up your account</h1>
              <p className="login__lede">
                You have been invited to the {details.organisation} portal as{' '}
                <strong>{details.email}</strong>. Choose a password, then add a
                second factor.
              </p>

              {error && (
                <div className="notice notice--danger" role="alert">
                  {Icon.alert({ size: 15 })}
                  <span>{error}</span>
                </div>
              )}

              <div className="field">
                <label className="field__label" htmlFor="invite-name">
                  Your full name
                </label>
                <input
                  id="invite-name"
                  className="input input--lg"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="invite-password">
                  Password
                </label>
                <input
                  id="invite-password"
                  className="input input--lg"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="field__hint">
                  A few unrelated words are easier to remember and harder to break
                  than something short with symbols in it. Checked against known
                  breaches.
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                disabled={busy || !name || !password}
              >
                {busy ? 'Setting up…' : 'Continue'}
              </Button>
            </form>
          )}
        </div>
      </section>

      <aside className="login__aside">
        <div className="login__asideinner">
          <h2 className="login__headline">
            Welcome to
            <br />
            the portal.
          </h2>
          <ul className="login__points">
            <li>{Icon.check({ size: 15 })} Write and track merchant applications</li>
            <li>{Icon.check({ size: 15 })} Residuals, splits and statements</li>
            <li>{Icon.check({ size: 15 })} Equipment and supplies ordering</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
